import React from "react";

import {
  Card,
  EmptyState,
  FlowChart,
  Segmented,
  Select,
  Skeleton,
  StatStrip,
  StatTile,
} from "../../design";
import { getFlow } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import { getHashParam, setHashParam } from "../../lib/hashState";
import type {
  DateRange,
  FlowBy,
  FlowMeasure,
  InsightFilters,
} from "../../lib/types";
import { flowGraph, formatMeasure } from "./flowGraph";
import { pct, shortRepo, useLoad } from "./util";

const BASE_BY: { value: FlowBy; label: string; none: string }[] = [
  { value: "team", label: "Team", none: "No team" },
  { value: "user", label: "Person", none: "Unattributed" },
  { value: "kind", label: "Agent", none: "Unknown agent" },
  { value: "repo", label: "Repository", none: "No repo" },
  { value: "tenant", label: "Customer", none: "No customer" },
];
const DEVICE_BY = {
  value: "device",
  label: "Device",
  none: "No device",
} as const;

const MEASURES: { value: FlowMeasure; label: string }[] = [
  { value: "tokens", label: "Tokens" },
  { value: "cost", label: "Spend" },
  { value: "requests", label: "Requests" },
];

/** Where usage goes: who sent it, which model served it, and how it's paid.
 * ``controls`` (the filter picker) joins the view's own control row. */
export function FlowView({
  authHeaders,
  base,
  range,
  filters = {},
  controls,
  devices = false,
}: {
  authHeaders: AuthHeaders;
  base: string;
  range: DateRange;
  filters?: InsightFilters;
  controls?: React.ReactNode;
  /** Offer the Enterprise-only device grouping. */
  devices?: boolean;
}) {
  const BY = devices ? [...BASE_BY, DEVICE_BY] : BASE_BY;
  const [by, setByState] = React.useState<FlowBy>(() => {
    const h = getHashParam("by");
    return BY.find((o) => o.value === h)?.value ?? "team";
  });
  const [measure, setMeasureState] = React.useState<FlowMeasure>(() => {
    const h = getHashParam("measure");
    return MEASURES.find((o) => o.value === h)?.value ?? "cost";
  });
  const setBy = (v: FlowBy) => {
    setByState(v);
    setHashParam("by", v === "team" ? null : v);
  };
  const setMeasure = (v: FlowMeasure) => {
    setMeasureState(v);
    setHashParam("measure", v === "cost" ? null : v);
  };
  const { data, error } = useLoad(
    () => getFlow(authHeaders, base, range, by, measure, filters),
    [base, range.start, range.end, by, measure, JSON.stringify(filters)],
  );
  const byOpt = BY.find((o) => o.value === by) ?? BY[0];
  const graph = React.useMemo(
    () =>
      data
        ? flowGraph(
            by === "repo"
              ? data.rows.map((r) => ({ ...r, label: shortRepo(r.label) }))
              : data.rows,
            byOpt.none,
          )
        : null,
    [data, by, byOpt.none],
  );
  const fmt = (v: number) => formatMeasure(measure, v);
  const byLabel = byOpt.label;
  const measureLabel = MEASURES.find((o) => o.value === measure)?.label ?? "";

  const total = data?.rows.reduce((s, r) => s + r.value, 0) ?? 0;
  const plan =
    data?.rows
      .filter((r) => r.cost_basis === "api_equivalent")
      .reduce((s, r) => s + r.value, 0) ?? 0;
  const tallest = graph
    ? Math.max(
        ...[0, 1, 2].map(
          (c) => graph.nodes.filter((n) => n.column === c).length,
        ),
      )
    : 0;

  return (
    <>
      <div className="ana-chips">
        <Select label="By" value={by} options={BY} onChange={setBy} />
        <Segmented
          size="sm"
          toggle
          label="Measure"
          value={measure}
          options={MEASURES}
          onChange={setMeasure}
        />
        {controls}
      </div>
      {data && total > 0 && (
        <StatStrip>
          <StatTile
            label={measureLabel}
            value={fmt(total)}
            note={
              by === "team"
                ? "Someone in two teams counts in both"
                : "In this range"
            }
          />
          {plan > 0 && plan < total && (
            <StatTile
              label="Billed per token"
              value={pct((total - plan) / total)}
              note={fmt(total - plan)}
            />
          )}
          {plan > 0 && plan < total && (
            <StatTile
              label="Covered by a plan"
              value={pct(plan / total)}
              note={fmt(plan)}
            />
          )}
        </StatStrip>
      )}
      <Card
        title={`${measureLabel} by ${byLabel.toLowerCase()}, model and billing`}
        info="Each band is one slice of usage, from who sent it, to the model that served it, to how it is paid: per token on your own provider key, or covered by a Claude or ChatGPT plan."
      >
        {error ? (
          <EmptyState compact icon="alert" title="Couldn't load the flow">
            {error}
          </EmptyState>
        ) : !data || !graph ? (
          <Skeleton lines={8} />
        ) : total === 0 ? (
          <EmptyState compact title="Nothing in this range" />
        ) : (
          <FlowChart
            nodes={graph.nodes}
            links={graph.links}
            format={fmt}
            height={Math.max(360, tallest * 58)}
            label={`${measureLabel} by ${byLabel.toLowerCase()}, model and billing`}
            columns={[byLabel, "Model", "Billing"]}
            unit={measureLabel.toLowerCase()}
          />
        )}
      </Card>
    </>
  );
}
